import { useEffect, useRef } from "react";
import useCustomPreview from "../../hooks/useCustomPreview";
import PermissionsChecker from "./Permissions-checker";
import { DEVICE_ACCESS_STATUS } from "../../components/VideoStream/constants";

/**
 * WaitingRoomPreview
 *
 * Renders the live camera preview in the demo waiting room.
 * Responsible for initializing and cleaning up the media preview
 * while handling device permission checks.
 *
 * This component is isolated to prevent unnecessary re-renders
 * in the main WaitingRoom component.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.setCameraMicrophonePermission - Updates camera/microphone permission state
 * @param {Function} props.setIsFrontCamera - Sets camera orientation (front/back)
 * @param {Function} props.cleanupStreams - Stops and cleans up active media streams
 * @returns {JSX.Element}
 */

async function detectIfFrontCamera() {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoInputs = devices.filter(d => d.kind === "videoinput");

    if (!videoInputs.length) return true;

    const labeled = videoInputs.find(d => d.label);
    const target = labeled || videoInputs[0];
    const label = (target.label || "").toLowerCase();

    if (label.includes("front") || label.includes("user")) return true;
    if (label.includes("back") || label.includes("rear") || label.includes("environment")) return false;

    return true;
  } catch {
    return true;
  }
}

export default function WaitingRoomPreview({
  setCameraMicrophonePermission,
  setIsFrontCamera,
  cleanupStreams,
}) {
  const containerRef = useRef();

  const {
    createPreview,
    destroyPreview,
    accessAllowed,
  } = useCustomPreview();

  useEffect(() => {
    let mounted = true;

    async function startPreview() {
      if (!containerRef.current) return;

      const isFront = await detectIfFrontCamera();
      if (!mounted) return;

      setIsFrontCamera(isFront);
      createPreview(containerRef.current);
    }

    startPreview();

    return () => {
      mounted = false;
      destroyPreview();
      cleanupStreams();
    };
  }, []);

  // Sync actual permission state to parent
  useEffect(() => {
    const hasPermission =
      accessAllowed === DEVICE_ACCESS_STATUS.ACCEPTED;

    setCameraMicrophonePermission(hasPermission);
  }, [accessAllowed, setCameraMicrophonePermission]);

  return (
    <>
      <PermissionsChecker />
      <div
        ref={containerRef}
        className="d-flex justify-content-center"
        id="waiting-room-video-container"
      />
    </>
  );
}